import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";

const DataGridSkeletonForEquipment = ({
    showAdminFilters = true,
    showCustomerFilters = true,
    columns = 6,
    rows = 8,
    showPagination = true,
}) => {
    return (
        <Box sx={{ width: '100%', p: 3 }}>
            {/* Page Title */}
            <Box sx={{ mb: 4 }}>
                <Skeleton
                    variant="text"
                    width={300}
                    height={45}
                    sx={{ fontSize: '1.8rem', fontWeight: 600 }}
                />
            </Box>

            {/* Search Bar */}
            <Box sx={{ mb: 2 }}>
                <Skeleton
                    variant="rectangular"
                    width="30%"
                    height={30}
                    sx={{ borderRadius: "12px", maxWidth: '600px' }}
                />
            </Box>

            {/* Main Table Container */}
            <Box sx={{
                borderRadius: "12px",
                backgroundColor: (theme) => theme.palette.background.paper,
                border: "1px solid rgba(0,0,0,0.12)",
                overflow: "hidden",
            }}>
                {/* Table Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 3,
                        py: 2.5,
                        backgroundColor: "rgba(0,0,0,0.03)",
                        borderBottom: "2px solid rgba(0,0,0,0.12)",
                    }}
                >
                    {/* Checkbox */}
                    <Box sx={{ width: 60, mr: 2 }}>
                        <Skeleton variant="rectangular" width={20} height={20} />
                    </Box>

                    {/* Customer Number */}
                    <Box sx={{ flex: 1.2, pr: 2 }}>
                        <Skeleton variant="text" height={26} width="75%" sx={{ fontWeight: 600 }} />
                    </Box>

                    {/* Site Number */}
                    <Box sx={{ flex: 1.2, pr: 2 }}>
                        <Skeleton variant="text" height={26} width="75%" sx={{ fontWeight: 600 }} />
                    </Box>

                    {/* Site Name */}
                    <Box sx={{ flex: 1.5, pr: 2 }}>
                        <Skeleton variant="text" height={26} width="65%" sx={{ fontWeight: 600 }} />
                    </Box>

                    {/* Machine Name */}
                    <Box sx={{ flex: 1.5, pr: 2 }}>
                        <Skeleton variant="text" height={26} width="70%" sx={{ fontWeight: 600 }} />
                    </Box>

                    {/* Machine Number */}
                    <Box sx={{ flex: 1.4, pr: 2 }}>
                        <Skeleton variant="text" height={26} width="80%" sx={{ fontWeight: 600 }} />
                    </Box>

                    {/* Status */}
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Skeleton variant="text" height={26} width={60} sx={{ mx: 'auto', fontWeight: 600 }} />
                    </Box>

                    {/* Actions */}
                    <Box sx={{ width: 140 }}>
                        <Skeleton variant="text" height={26} width={70} sx={{ mx: 'auto', fontWeight: 600 }} />
                    </Box>
                </Box>

                {/* Table Body */}
                {Array.from({ length: rows }).map((_, rowIdx) => (
                    <Box
                        key={`row-${rowIdx}`}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 3,
                            py: 2.2,
                            borderBottom: rowIdx !== rows - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
                            '&:hover': {
                                backgroundColor: "rgba(0,121,255,0.04)",
                            }
                        }}
                    >
                        {/* Checkbox */}
                        <Box sx={{ width: 60, mr: 2 }}>
                            <Skeleton variant="rectangular" width={20} height={20} />
                        </Box>

                        {/* Customer Number */}
                        <Box sx={{ flex: 1.2, pr: 1 }}>
                            <Skeleton
                                variant="rectangular"
                                height={20}
                                width={`${75 - rowIdx * 1}%`}
                                sx={{ borderRadius: "4px" }}
                            />
                        </Box>

                        {/* Site Number */}
                        <Box sx={{ flex: 1.2, pr: 1 }}>
                            <Skeleton
                                variant="rectangular"
                                height={20}
                                width={`${70 - rowIdx * 0.8}%`}
                                sx={{ borderRadius: "4px" }}
                            />
                        </Box>

                        {/* Site Name */}
                        <Box sx={{ flex: 1.5, pr: 1 }}>
                            <Skeleton
                                variant="rectangular"
                                height={20}
                                width={`${65 - rowIdx * 0.5}%`}
                                sx={{ borderRadius: "4px" }}
                            />
                        </Box>

                        {/* Machine Name */}
                        <Box sx={{ flex: 1.5, pr: 1 }}>
                            <Skeleton
                                variant="rectangular"
                                height={20}
                                width={`${70 - rowIdx * 0.6}%`}
                                sx={{ borderRadius: "4px" }}
                            />
                        </Box>

                        {/* Machine Number */}
                        <Box sx={{ flex: 1.4, pr: 1 }}>
                            <Skeleton
                                variant="rectangular"
                                height={20}
                                width="85%"
                                sx={{ borderRadius: "4px" }}
                            />
                        </Box>

                        {/* Status Chip */}
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Skeleton
                                variant="rectangular"
                                width={70}
                                height={24}
                                sx={{ borderRadius: "12px", mx: 'auto' }}
                            />
                        </Box>

                        {/* Actions */}
                        <Box sx={{ width: 140, display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                            <Skeleton variant="circular" width={32} height={32} />
                            <Skeleton variant="circular" width={32} height={32} />
                        </Box>
                    </Box>
                ))}

                {/* Pagination Footer */}
                {showPagination && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 3,
                            py: 2,
                            borderTop: "1px solid rgba(0,0,0,0.12)",
                            backgroundColor: "rgba(0,0,0,0.02)",
                        }}
                    >
                        <Skeleton variant="text" width={160} height={22} />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Skeleton variant="text" width={120} height={22} />
                            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: "6px" }} />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Skeleton variant="circular" width={36} height={36} />
                                <Skeleton variant="circular" width={36} height={36} />
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default DataGridSkeletonForEquipment;